'use client';

import { Pagination as HeroUiPagination, type PaginationProps } from '@heroui/pagination';
import type { PressEvent } from '@react-types/shared';
import { forwardRef, useCallback } from 'react';

type PaginationRenderItem = NonNullable<PaginationProps['renderItem']>;

const PAGINATION_PREV = 'prev';
const PAGINATION_NEXT = 'next';

/**
 * Defaults `disableCursorAnimation` so HeroUI does not render a non-`<li>` child inside `<ul>`
 * (pagination cursor span), which breaks list semantics for screen readers / audits.
 *
 * Renders each page control as `<li><button>` instead of `<li role="button">` for agentic
 * browsing / accessibility tree compliance.
 */
export const Pagination = forwardRef<HTMLElement, PaginationProps>(function Pagination(
  { disableCursorAnimation = true, renderItem: renderItemProp, loop = false, ...props },
  ref,
) {
  const accessibleRenderItem = useCallback<PaginationRenderItem>(
    item => {
      if (renderItemProp) {
        return renderItemProp(item);
      }

      const isPrev = item.value === PAGINATION_PREV;
      const isNext = item.value === PAGINATION_NEXT;
      const isDisabled =
        (!loop && isPrev && item.activePage === 1) ||
        (!loop && isNext && item.activePage === item.total);

      return (
        <li key={item.key} className="list-none">
          <button
            type="button"
            className={item.className}
            aria-label={item.getAriaLabel?.(item.value)}
            aria-current={item.isActive ? 'page' : undefined}
            disabled={isDisabled}
            onClick={() => item.onPress?.({} as PressEvent)}
            ref={typeof item.value === 'number' ? item.ref : undefined}
          >
            {item.children}
          </button>
        </li>
      );
    },
    [loop, renderItemProp],
  );

  return (
    <HeroUiPagination
      ref={ref}
      disableCursorAnimation={disableCursorAnimation}
      loop={loop}
      renderItem={accessibleRenderItem}
      {...props}
    />
  );
});
